import { Button, Flex, Heading, Skeleton, Spacer } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { GenericError } from "src/components/Feedback/GenericError";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import {
  NavigationBlocker,
  NavigationBlockerProvider,
} from "src/components/NavigationBlocker";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import * as RD from "src/types/remoteData";
import { z } from "zod";
import * as Explore from "./ExploreList";
import * as Match from "./Match";

export const LicensesAndBillingTab = () => {
  const match = Match.useForm();
  const explore = Explore.useForm();
  const toast = useAvelaToast();

  return (
    <RemoteDataView
      remoteData={RD.toTuple(match.initialValues, explore.initialValues)}
      error={() => <GenericError />}
      loading={<Loading />}
    >
      {([matchInitialValues, exploreInitialValues]) => {
        return (
          <NavigationBlockerProvider>
            <Formik<FormikType>
              initialValues={{ ...matchInitialValues, ...exploreInitialValues }}
              onSubmit={async (values, formik) => {
                try {
                  await match.submit(values);
                  formik.resetForm({ values });
                  toast({
                    title: "Organization updated",
                    isClosable: true,
                    status: "success",
                  });
                } catch (error) {
                  console.error(error);
                  toast.error({
                    title: "Error editing organization",
                    description:
                      "Please try again later or report the problem to our support team",
                  });
                }
              }}
            >
              {(formik) => {
                return (
                  <Flex
                    as={Form}
                    direction="column"
                    height="100%"
                    grow="1"
                    gap={6}
                  >
                    <Heading as="h2" variant="admin">
                      License & Billing
                    </Heading>
                    <Flex direction="column" gap={4}>
                      <Match.Form />
                      <Explore.ExploreList data={explore.data} />
                    </Flex>
                    <Spacer />
                    <AdminFormButtons insideTabPanel>
                      <Spacer />
                      <Button
                        type="submit"
                        isLoading={formik.isSubmitting}
                        isDisabled={
                          !formik.dirty ||
                          !formik.isValid ||
                          formik.isSubmitting
                        }
                      >
                        Update
                      </Button>
                      <NavigationBlocker hasChanges={formik.dirty} />
                    </AdminFormButtons>
                  </Flex>
                );
              }}
            </Formik>
          </NavigationBlockerProvider>
        );
      }}
    </RemoteDataView>
  );
};

const _FormikSchema = Match.FormikSchema;
type FormikType = z.infer<typeof _FormikSchema>;
const Loading = () => {
  return (
    <Flex direction="column" gap={6}>
      <Skeleton height="2rem" width="15rem" />
      <Skeleton height="2rem" width="5rem" />
      <Skeleton height="2rem" width="5rem" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
    </Flex>
  );
};
